import { useEffect, useRef, useState } from "react";

/**
 * Scroll-reveal trigger for the landing page.
 *
 * Returns [ref, inView]. Put the ref on a section root and switch a class on it
 * (`inView ? "in-view" : ""`); children marked `.reveal` in landing.scss then
 * animate in with their `--i` stagger. One observer drives a whole section, so
 * a page full of reveals costs a handful of observers, not one per element.
 *
 * Defaults trigger a little before the element is fully on screen and, by
 * design, only once - content should not re-hide when you scroll back up. Pass
 * once:false for elements you want to re-animate each time they re-enter.
 *
 * Degrades safe: with no IntersectionObserver (old browsers, SSR) it reports
 * visible immediately, so nothing can get stuck at opacity:0. The CSS also has
 * a prefers-reduced-motion branch that shows everything without motion.
 */
export default function useReveal({
    threshold = 0.15,
    rootMargin = "0px 0px -12% 0px",
    once = true,
} = {}) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        if (typeof IntersectionObserver === "undefined") {
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (once) observer.disconnect();
                } else if (!once) {
                    setInView(false);
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold, rootMargin, once]);

    return [ref, inView];
}
